import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Card, CardHeader, Nav, NavItem, NavLink, CardBody, Button, Col, Input, Row, Label, Modal, ModalHeader, ModalBody, ModalFooter, Form, TabPane, TabContent, Spinner, FormFeedback, FormGroup, Alert, CardFooter } from 'reactstrap';
import useFormDraft from '../../../Components/Hooks/useFormDraft';
import DraftStatusBar from '../../../Components/Common/DraftStatusBar';
import OSelect from '../../../Components/OElements/OSelect';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import classnames from "classnames";
import GeneralTab from '../Tabs/GeneralTab';
import ListOfFlowRates from '../Tabs/ListOfFlowRates';
import FlowInformation from '../Tabs/FlowInformation';
import ListDistancesAndElevation from '../Tabs/ListDistancesAndElevation';
import RegistrationAndProcessing from '../Tabs/RegistrationAndProcessing';
import RegistrationOfWaterOwnership from '../Tabs/RegistrationOfWaterOwnership';
import CPA_DGA from '../Tabs/CPA_DGA';
import Coordinates from '../Tabs/Coordinates';
import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from 'reselect';
import useFileType from '../../../Components/Hooks/useFileTypeHooks';
import { addNewWaterRights, updateWaterRights } from '../../../slices/thunks';
import { addNewWaterFlowRateListApi, deleteWaterFlowRateListApi } from '../../../helpers/water_flow_rate_list/api_helper';
import { addNewIntakeCoordinatesApi, deleteIntakeCoordinatesApi } from '../../../helpers/intake_coordinates/api_helper';
import { addNewRestitutionCoordinatesApi, deleteRestitutionCoordinatesApi } from '../../../helpers/restitution_coordinates/api_helper';
import { addNewDistanceElevationApi, deleteDistanceElevationApi } from '../../../helpers/distance_elevation/api_helper';
import { deleteWaterRights as deleteWaterRightsApi } from '../../../helpers/water_rights/backend_helper';
import { getWaterFlowRateListQuery } from '../../../slices/waterFlowRateList/thunk';
import { getIntakeCoordinatesListQuery } from '../../../slices/intakeCoordinates/thunk';
import { getRestitutionCoordinatesListQuery } from '../../../slices/restitutionCoordinates/thunk';
import { getDistanceElevationListQuery } from '../../../slices/distanceElevation/thunk';
import dayjs from 'dayjs';
import ResumeTab from '../Tabs/ResumeTab';
import { useLoading } from '../../../Context/LoadingContext';
import usePathFolder from '../../../Components/Hooks/usePathFolder';
import useUploadFiles from '../../../Components/Hooks/useUploadFiles';
import useLogoLoader from '../../../Components/Hooks/useLogoLoaderHooks';
import { mapBackendFieldError, normalizeApiError } from '../../../utils/mapBackendFieldError';
import { normalizeFlowRatePayload } from '../../../utils/normalizeFlowRatePayload';

const emptyToUndefined = (_value, originalValue) =>
	(originalValue === '' || originalValue === null ? undefined : originalValue);

const toOptionalNumber = (v) => {
	if (v === '' || v == null) return undefined;
	const n = Number(v);
	return Number.isNaN(n) ? undefined : n;
};

const unwrapApiData = (response) => response?.data ?? response;

/** ponytail: best-effort rollback on failed create; upgrade path: backend transaction */
const rollbackCreatedWaterRight = async (waterRightId, savedChildren) => {
	const deletions = [
		...savedChildren.flowRates.map((id) => deleteWaterFlowRateListApi(id)),
		...savedChildren.intake.map((id) => deleteIntakeCoordinatesApi(id)),
		...savedChildren.restitution.map((id) => deleteRestitutionCoordinatesApi(id)),
		...savedChildren.distanceElevation.map((id) => deleteDistanceElevationApi(id)),
	];
	await Promise.allSettled(deletions);
	try {
		await deleteWaterRightsApi({ _id: waterRightId });
	} catch (e) {
		console.error('Rollback: no se pudo eliminar el derecho de agua parcial', e);
	}
};

const TAB_ERROR_FIELDS = {
	7: ['establishment_id', 'region_id', 'commune_id', 'province_id', 'company_id', 'resolution_number', 'resolution_date', 'file_number', 'water_rights_status_id', 'water_right_type_id', 'water_source_type_id', 'water_use_type_id', 'water_use_status_id', 'installation_location_ids', 'water_source_id'],
	9: ['has_annual_average_flow', 'annual_average_flow', 'flow_rate_unit_id', 'has_ecological_flow', 'ecological_flow'],
	10: ['works_registration_date', 'report_transmission_start', 'resolution_name', 'resolution_region_id'],
	13: ['real_estate_registry_id', 'fojas', 'cbr_number', 'cbr_year'],
	14: ['certificate_number', 'certificate_year'],
};

const WIZARD_TABS = [7, 8, 9, 10, 11, 12, 13, 14];

const WIZARD_TAB_ITEMS = [
	{ id: 7, label: 'Datos Generales' },
	{ id: 8, label: 'Distribución de Agua Autorizada' },
	{ id: 9, label: 'Información del Caudal' },
	{ id: 10, label: 'Registro y Tramitación' },
	{ id: 11, label: 'Coordenadas' },
	{ id: 12, label: 'Distancia y Desnivel' },
	{ id: 13, label: 'Registro de Propiedad de Agua C.B.R.' },
	{ id: 14, label: 'CPA DGA' },
];

const FlowRatesForm = (props) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { openLoading, closeLoading } = useLoading();

    // Obtener datos de edición desde location.state y congelarlos en useState
    // (location.state se limpia cuando Link to='#' hace una navegación interna)
    const [waterRightData] = useState(location.state?.waterRightData || null);
    const [isEdit] = useState(location.state?.isEdit || false);

    const [selectedSector, setSelectedSector] = useState(null);
    const handleSectorSelected = (optionSelected) => {
        setSelectedSector(optionSelected);
    };

    // Arrays para ODataGrids internos
    const [flowRatesList, setFlowRatesList] = useState([]);
    const [intakeCoordinatesList, setIntakeCoordinatesList] = useState([]);
    const [restitutionCoordinatesList, setRestitutionCoordinatesList] = useState([]);
    const [distanceElevationList, setDistanceElevationList] = useState([]);

    // Get current user from localStorage
    const getCurrentUser = () => {
        try {
            const authUser = JSON.parse(localStorage.getItem('authUser'));
            return authUser?.user?.email || authUser?.email || 'system';
        } catch (error) {
            console.error('Error getting current user:', error);
            return 'system';
        }
    };

    // Helper function to parse dates - converts string dates to Date objects for Mantine DateInput
    const parseDate = (dateValue) => {
        if (!dateValue) return null;
        if (dateValue instanceof Date) return dateValue;
        const parsed = new Date(dateValue);
        return isNaN(parsed.getTime()) ? null : parsed;
    };

    // Helper para extraer año de diferentes formatos
    const parseYear = (yearValue) => {
        if (!yearValue) return null;
        // Si ya es un número, retornarlo
        if (typeof yearValue === 'number') return yearValue;
        // Si es una fecha string o Date object, extraer el año
        if (typeof yearValue === 'string' || yearValue instanceof Date) {
            const date = new Date(yearValue);
            if (!isNaN(date.getTime())) {
                return date.getFullYear();
            }
        }
        // Intentar convertir a número directamente
        const num = Number(yearValue);
        return isNaN(num) ? null : num;
    };

    // Helper para extraer el _id de un campo que puede ser un objeto poblado o un ID string
    const extractId = (field) => {
        if (!field) return '';
        if (typeof field === 'string') return field;         // raw ID
        if (typeof field === 'object') return field._id || field.id || '';
        return '';
    };

    // Elimina cualquier campo cuyo valor sea un objeto poblado (join de MongoDB) antes de enviarlo al backend.
    // Esto evita que el backend reciba ObjectIds no serializables en el payload.
    const sanitizePayload = (data) => {
        const cleaned = {};
        for (const [key, val] of Object.entries(data)) {
            if (Array.isArray(val)) {
                // Preserve arrays as-is (e.g. water_source_ids)
                cleaned[key] = val;
            } else if (val !== null && val !== undefined && typeof val === 'object' && !(val instanceof Date)) {
                // Objeto poblado — extraer solo el _id si existe, de lo contrario omitir
                const id = val._id || val.id;
                if (id) cleaned[key] = String(id);
                // Si no tiene _id/id, se omite completamente
            } else {
                cleaned[key] = val;
            }
        }
        return cleaned;
    };

    // ponytail: omite null/'' en create; en update envía null para limpiar campos opcionales en backend
    const normalizeWaterRightPayload = (data, isUpdate = false) => {
        const cleaned = sanitizePayload(data);
        const clearOptional = (key) => {
            if (cleaned[key] == null || cleaned[key] === '') {
                if (isUpdate) cleaned[key] = null;
                else delete cleaned[key];
            }
        };
        ['real_estate_registry_id', 'resolution_region_id', 'province_id', 'flow_rate_unit_id'].forEach(clearOptional);
        ['fojas', 'cbr_number', 'certificate_number', 'resolution_name'].forEach(clearOptional);
        ['cbr_year', 'certificate_year', 'works_registration_date', 'report_transmission_start'].forEach(key => {
            if (cleaned[key] == null || cleaned[key] === '') {
                if (isUpdate) cleaned[key] = null;
                else delete cleaned[key];
            }
        });
        if (!cleaned.has_ecological_flow_rate) delete cleaned.ecological_flow_rate;
        if (!cleaned.has_annual_flow_rate) {
            delete cleaned.annual_average_flow_rate;
            delete cleaned.flow_rate_unit_id;
        }
        return cleaned;
    };

    const draftStorageKey = !isEdit ? `water_right_draft_${getCurrentUser()}` : null;
    const { draftData, saveDraft, clearDraft, hasDraft, lastSaved, draftActiveTab } = useFormDraft(draftStorageKey);
    const [resumeDraftModalOpen, setResumeDraftModalOpen] = useState(false);
    const [discardDraftModalOpen, setDiscardDraftModalOpen] = useState(false);

    const { file_type } = useFileType('RES');

    const validation = useFormik({
        // enableReinitialize: use this flag when initial values needs to be changed
        enableReinitialize: true,
        initialValues: {
            // Tab 1 - Datos Generales
            establishment_id: waterRightData?.establishment?._id || waterRightData?.establishment_id || '',
            region_id: extractId(waterRightData?.region) || waterRightData?.region_id || '',
            commune_id: waterRightData?.commune?._id || waterRightData?.commune_id || '',
            province_id: waterRightData?.province?._id || waterRightData?.province_id || '',
            company_id: waterRightData?.company?._id || waterRightData?.company_id || '',
            resolution_number: waterRightData?.resolution_number || '',
            resolution_date: parseDate(waterRightData?.resolution_date),
            file_number: waterRightData?.file_number || '',
            water_rights_status_id: waterRightData?.water_rights_status?._id || waterRightData?.water_rights_status_id || '',
            water_right_type_id: waterRightData?.water_right_type?._id || waterRightData?.water_right_type_id || '',
            water_source_type_id: waterRightData?.water_source_type?._id || waterRightData?.water_source_type_id || '',
            water_use_type_id: waterRightData?.water_use_type?._id || waterRightData?.water_use_type_id || '',
            water_use_status_id: waterRightData?.water_use_status?._id || waterRightData?.water_use_status_id || '',
            installation_location_ids: Array.isArray(waterRightData?.installation_location)
                ? waterRightData.installation_location.map(s => s._id).filter(Boolean)
                : (waterRightData?.installation_location_ids || []),
            water_source_id: Array.isArray(waterRightData?.water_source)
                ? waterRightData.water_source.map(s => s._id).filter(Boolean)
                : [],

            // Tab 3 - Información del Caudal
            has_annual_average_flow: waterRightData?.has_annual_flow_rate || (waterRightData?.annual_average_flow_rate != null && waterRightData?.annual_average_flow_rate !== '' ? true : false),
            annual_average_flow: waterRightData?.annual_average_flow_rate ?? '',
            flow_rate_unit_id: waterRightData?.flow_rate_unit?._id || waterRightData?.flow_rate_unit_id || '',
            has_ecological_flow: waterRightData?.has_ecological_flow_rate || (waterRightData?.ecological_flow_rate != null && waterRightData?.ecological_flow_rate !== '' ? true : false),
            ecological_flow: waterRightData?.ecological_flow_rate ?? '',

            // Tab 4 - Registro y Tramitación
            works_registration_date: parseDate(waterRightData?.works_registration_date),
            report_transmission_start: parseDate(waterRightData?.report_transmission_start),
            resolution_name: waterRightData?.resolution_name || '',
            resolution_region_id: waterRightData?.resolution_region?._id || waterRightData?.resolution_region_id || '',

            // Tab 7 - Registro de Propiedad
            real_estate_registry_id: waterRightData?.real_estate_registry?._id || waterRightData?.real_estate_registry_id || '',
            fojas: waterRightData?.fojas || '',
            cbr_number: waterRightData?.cbr_number || '',
            cbr_year: parseYear(waterRightData?.cbr_year),

            // Tab 8 - CPA DGA
            certificate_number: waterRightData?.certificate_number || '',
            certificate_year: parseYear(waterRightData?.certificate_year),
        },
        validationSchema: Yup.object({
            // Tab 1 - Datos Generales (campos requeridos)
            establishment_id: Yup.string().required('Campo requerido'),
            region_id: Yup.string().required('Campo requerido'),
            commune_id: Yup.string().required('Campo requerido'),
            province_id: Yup.string().nullable().notRequired(), // Opcional - se infiere de la comuna
            company_id: Yup.string().required('Campo requerido'),
            resolution_number: Yup.string().required('Campo requerido').max(100, 'Máximo 100 caracteres'),
            resolution_date: Yup.date().required('Campo requerido').nullable(),
            file_number: Yup.string().required('Campo requerido').max(100, 'Máximo 100 caracteres'),
            water_rights_status_id: Yup.string().required('Campo requerido'),
            water_right_type_id: Yup.string().required('Campo requerido'),
            water_source_type_id: Yup.string().required('Campo requerido'),
            water_use_type_id: Yup.string().required('Campo requerido'),
            water_use_status_id: Yup.string().required('Campo requerido'),
            installation_location_ids: Yup.array().min(1, 'Campo requerido'),
            water_source_id: Yup.array().min(1, 'Campo requerido'),

            // Tab 3 - Información del Caudal (validaciones condicionales)
            has_annual_average_flow: Yup.boolean(),
            annual_average_flow: Yup.number()
                .transform(emptyToUndefined)
                .when('has_annual_average_flow', {
                    is: true,
                    then: () => Yup.number().required('Campo requerido').min(0, 'Debe ser mayor o igual a 0'),
                    otherwise: () => Yup.number().nullable()
                }),
            flow_rate_unit_id: Yup.string().when('has_annual_average_flow', {
                is: true,
                then: () => Yup.string().required('Campo requerido'),
                otherwise: () => Yup.string().nullable()
            }),
            has_ecological_flow: Yup.boolean(),
            ecological_flow: Yup.number()
                .transform(emptyToUndefined)
                .when('has_ecological_flow', {
                    is: true,
                    then: () => Yup.number().required('Campo requerido').min(0, 'Debe ser mayor o igual a 0'),
                    otherwise: () => Yup.number().nullable()
                }),

            // Tab 4 - Registro y Tramitación
            works_registration_date: Yup.date().nullable(),
            report_transmission_start: Yup.date()
                .nullable()
                .when('works_registration_date', {
                    is: (val) => val != null,
                    then: () => Yup.date()
                        .nullable()
                        .min(Yup.ref('works_registration_date'), 'Debe ser posterior a la fecha de inscripción de obras'),
                    otherwise: () => Yup.date().nullable()
                }),
            resolution_name: Yup.string().max(200, 'Máximo 200 caracteres').nullable().notRequired(),
            resolution_region_id: Yup.string().nullable().notRequired(),

            // Tab 7 - Registro de Propiedad (campos opcionales)
            real_estate_registry_id: Yup.string().nullable().notRequired(),
            fojas: Yup.string().max(50, 'Máximo 50 caracteres'),
            cbr_number: Yup.string().max(50, 'Máximo 50 caracteres'),
            cbr_year: Yup.number()
                .nullable()
                .min(1900, 'Año inválido')
                .max(new Date().getFullYear(), `Año no puede ser mayor a ${new Date().getFullYear()}`),

            // Tab 8 - CPA DGA (campos opcionales)
            certificate_number: Yup.string().max(100, 'Máximo 100 caracteres'),
            certificate_year: Yup.number()
                .nullable()
                .min(1900, 'Año inválido')
                .max(new Date().getFullYear(), `Año no puede ser mayor a ${new Date().getFullYear()}`),
        }),
        onSubmit: async (values) => {
            // Validaciones personalizadas para arrays
            if (flowRatesList.length === 0) {
                setTabStatuses((prev) => ({ ...prev, 8: 'error' }));
                toast.warning('Debe agregar al menos un caudal en la pestaña "Distribución de Agua Autorizada"', { autoClose: 5000 });
                toggleVerticalTab(8);
                return;
            }

            if (intakeCoordinatesList.length === 0 && restitutionCoordinatesList.length === 0) {
                setTabStatuses((prev) => ({ ...prev, 11: 'error' }));
                toast.warning('Debe agregar al menos una coordenada de captación o restitución en la pestaña "Coordenadas"', { autoClose: 5000 });
                toggleVerticalTab(11);
                return;
            }

            openLoading();

            try {
                const currentUser = getCurrentUser();

                // Construir payload del water_right (todos los campos de Formik ya usan nombres del backend)
                const waterRightPayload = {
                    ...values,
                    // Mapear campos internos del formulario a nombres del backend
                    annual_average_flow_rate: toOptionalNumber(values.annual_average_flow),
                    has_annual_flow_rate: values.has_annual_average_flow,
                    ecological_flow_rate: toOptionalNumber(values.ecological_flow),
                    has_ecological_flow_rate: values.has_ecological_flow,
                    water_source_ids: values.water_source_id || [],
                };

                // Eliminar campos internos del formulario que ya fueron mapeados
                delete waterRightPayload.annual_average_flow;
                delete waterRightPayload.has_annual_average_flow;
                delete waterRightPayload.ecological_flow;
                delete waterRightPayload.has_ecological_flow;
                delete waterRightPayload.water_source_id;

                if (isEdit && waterRightData?._id) {
                    waterRightPayload._id = waterRightData._id;
                    waterRightPayload.creation_user = waterRightData.creation_user || currentUser;
                    waterRightPayload.creation_date = waterRightData.creation_date || new Date().toISOString();
                    waterRightPayload.modification_user = currentUser;
                    waterRightPayload.modification_date = new Date().toISOString();
                } else {
                    waterRightPayload.creation_user = currentUser;
                    waterRightPayload.creation_date = new Date().toISOString();
                }

                // Paso 1: Guardar el water_right principal PRIMERO para obtener su _id
                const payload = normalizeWaterRightPayload(waterRightPayload, !!(isEdit && waterRightData?._id));
                let waterRightResponse;
                if (isEdit && waterRightData?._id) {
                    waterRightResponse = await dispatch(updateWaterRights(payload)).unwrap();
                } else {
                    waterRightResponse = await dispatch(addNewWaterRights(payload)).unwrap();
                }

                const savedWaterRightId = waterRightResponse?._id || waterRightData?._id;
                const savedChildren = { flowRates: [], intake: [], restitution: [], distanceElevation: [] };

                const failChildSave = (tab, cause) => {
                    throw { childSave: true, tab, cause, savedWaterRightId, savedChildren };
                };

                // Paso 2: Guardar children nuevos (sin _id) con water_right_id — sin toasts por ítem
                for (const item of flowRatesList) {
                    if (!item._id) {
                        try {
                            const res = await addNewWaterFlowRateListApi(
                                normalizeFlowRatePayload({ ...item, water_right_id: savedWaterRightId })
                            );
                            const saved = unwrapApiData(res);
                            if (saved?._id) savedChildren.flowRates.push(saved._id);
                        } catch (error) {
                            console.error('Error al guardar caudal:', error);
                            failChildSave(8, error);
                        }
                    }
                }

                // Build name→id maps while saving coordinates (needed to resolve draft references in distanceElevation)
                const intakeNameToId = {};
                for (const item of intakeCoordinatesList) {
                    if (!item._id) {
                        try {
                            const res = await addNewIntakeCoordinatesApi({ ...item, water_right_id: savedWaterRightId });
                            const saved = unwrapApiData(res);
                            if (saved?._id) savedChildren.intake.push(saved._id);
                            if (item.name && saved?._id) intakeNameToId[item.name] = saved._id;
                        } catch (error) {
                            console.error('Error al guardar coordenada de captación:', error);
                            failChildSave(11, error);
                        }
                    } else if (item.name) {
                        intakeNameToId[item.name] = item._id;
                    }
                }

                const restitutionNameToId = {};
                for (const item of restitutionCoordinatesList) {
                    if (!item._id) {
                        try {
                            const res = await addNewRestitutionCoordinatesApi({ ...item, water_right_id: savedWaterRightId });
                            const saved = unwrapApiData(res);
                            if (saved?._id) savedChildren.restitution.push(saved._id);
                            if (item.name && saved?._id) restitutionNameToId[item.name] = saved._id;
                        } catch (error) {
                            console.error('Error al guardar coordenada de restitución:', error);
                            failChildSave(11, error);
                        }
                    } else if (item.name) {
                        restitutionNameToId[item.name] = item._id;
                    }
                }

                const isMongoId = (val) => typeof val === 'string' && /^[0-9a-fA-F]{24}$/.test(val);

                for (const item of distanceElevationList) {
                    if (!item._id) {
                        try {
                            const resolvedItem = { ...item };
                            if (resolvedItem.intake_coordinates_id && !isMongoId(resolvedItem.intake_coordinates_id)) {
                                resolvedItem.intake_coordinates_id = intakeNameToId[resolvedItem.intake_coordinates_id] || undefined;
                            }
                            if (resolvedItem.restitution_coordinates_id && !isMongoId(resolvedItem.restitution_coordinates_id)) {
                                resolvedItem.restitution_coordinates_id = restitutionNameToId[resolvedItem.restitution_coordinates_id] || undefined;
                            }
                            const res = await addNewDistanceElevationApi({ ...resolvedItem, water_right_id: savedWaterRightId });
                            const saved = unwrapApiData(res);
                            if (saved?._id) savedChildren.distanceElevation.push(saved._id);
                        } catch (error) {
                            console.error('Error al guardar distancia y desnivel:', error);
                            failChildSave(12, error);
                        }
                    }
                }

                closeLoading();

                toast.success(isEdit ? 'Derecho de agua actualizado exitosamente' : 'Derecho de agua registrado exitosamente', { autoClose: 3000 });
                clearDraft();
                navigate('/water-right/list');

            } catch (error) {
                closeLoading();
                if (error?.childSave) {
                    if (!isEdit && error.savedWaterRightId) {
                        await rollbackCreatedWaterRight(error.savedWaterRightId, error.savedChildren);
                    }
                    const mapped = mapBackendFieldError(
                        normalizeApiError(error.cause?.response?.data || error.cause)
                    );
                    toast.error(
                        isEdit
                            ? `El derecho de agua se actualizó, pero hay datos relacionados con errores: ${mapped.message}`
                            : `No se registró el derecho de agua. ${mapped.message}`,
                        { autoClose: 6000 }
                    );
                    setTabStatuses((prev) => ({ ...prev, [error.tab]: 'error' }));
                    toggleVerticalTab(error.tab);
                    return;
                }
                console.error('Error al guardar derecho de agua:', error);
                const mapped = mapBackendFieldError(normalizeApiError(error));
                if (mapped.formField) {
                    validation.setFieldError(mapped.formField, mapped.message);
                    updateAllTabStatuses({ [mapped.formField]: mapped.message });
                    navigateToErrorTab([mapped.formField]);
                }
                toast.error(mapped.message, { autoClose: 5000 });
            }
        }
    });

    // useEffect para cargar datos de arrays cuando estamos en modo edición
    useEffect(() => {
        const loadArrayData = async () => {
            if (isEdit && waterRightData?._id) {
                try {
                    const baseQuery = {
                        filters: { water_right_id: { $eq: waterRightData._id }, disable: { $eq: false } },
                        pagination: { page: 1, pageSize: 500 },
                    };

                    const [flowRatesData, intakeData, restitutionData, distanceElevationData] = await Promise.all([
                        dispatch(getWaterFlowRateListQuery({
                            ...baseQuery,
                            populate: ['right_exercise_type', 'flow_rate_unit', 'water_source'],
                        })).unwrap(),
                        dispatch(getIntakeCoordinatesListQuery({
                            ...baseQuery,
                            populate: ['utm_zone', 'geodetic_datum'],
                        })).unwrap(),
                        dispatch(getRestitutionCoordinatesListQuery({
                            ...baseQuery,
                            populate: ['utm_zone', 'geodetic_datum'],
                        })).unwrap(),
                        dispatch(getDistanceElevationListQuery(baseQuery)).unwrap(),
                    ]);

                    setFlowRatesList(flowRatesData?.data || []);
                    setIntakeCoordinatesList(intakeData?.data || []);
                    setRestitutionCoordinatesList(restitutionData?.data || []);
                    setDistanceElevationList(distanceElevationData?.data || []);
                } catch (error) {
                    console.error('Error al cargar datos de arrays:', error);
                    toast.error('Error al cargar algunos datos. Por favor, recargue la página.', { autoClose: 5000 });
                }
            }
        };

        loadArrayData();
    }, [isEdit, waterRightData?._id, dispatch]);

    // Detect existing draft on mount and offer to resume (create mode only)
    useEffect(() => {
        if (!isEdit && hasDraft && draftData) {
            setResumeDraftModalOpen(true);
        }
    }, []); 

    const [activeTab, setactiveTab] = useState(1);
    const [activeArrowTab, setactiveArrowTab] = useState(4);
    const [activeVerticalTab, setactiveVerticalTab] = useState(7);
    const [progressbarvalue, setprogressbarvalue] = useState(0);
    const [passedSteps, setPassedSteps] = useState([1]);
    const [passedarrowSteps, setPassedarrowSteps] = useState([1]);
    const [passedverticalSteps, setPassedverticalSteps] = useState([1]);
    const [tabStatuses, setTabStatuses] = useState(() =>
        WIZARD_TABS.reduce((acc, id) => {
            acc[id] = isEdit ? 'valid' : 'pending';
            return acc;
        }, {})
    );

    const computeTabStatus = (tabId, errors) => {
        if (tabId === 8 && flowRatesList.length === 0) return 'error';
        if (tabId === 11 && intakeCoordinatesList.length === 0 && restitutionCoordinatesList.length === 0) return 'error';
        const fields = TAB_ERROR_FIELDS[tabId];
        if (fields?.some((f) => errors[f])) return 'error';
        return 'valid';
    };

    const updateAllTabStatuses = (errors) => {
        setTabStatuses(
            WIZARD_TABS.reduce((acc, tabId) => {
                acc[tabId] = computeTabStatus(tabId, errors);
                return acc;
            }, {})
        );
    };

    const validateAndMarkTab = async (tabId) => {
        const errors = await validation.validateForm();
        const status = computeTabStatus(tabId, errors);
        setTabStatuses((prev) => ({ ...prev, [tabId]: status }));
        if (status === 'error') {
            const fields = TAB_ERROR_FIELDS[tabId] || [];
            validation.setTouched({
                ...validation.touched,
                ...fields.reduce((acc, f) => ({ ...acc, [f]: true }), {}),
            });
        }
        return status !== 'error';
    };

    const getTabNavClass = (tabId) =>
        classnames({
            active: activeVerticalTab === tabId,
            done: tabStatuses[tabId] === 'valid',
            'tab-error': tabStatuses[tabId] === 'error',
        });

    const getTabIconClass = (tabId) => {
        const status = tabStatuses[tabId];
        if (status === 'error') return 'ri-close-circle-fill step-icon me-2';
        if (status === 'valid') return 'ri-close-circle-fill step-icon me-2';
        return 'ri-checkbox-blank-circle-line step-icon me-2';
    };

    const handleTabNavigation = async (tabId) => {
        if (!isEdit && activeVerticalTab !== tabId) {
            await validateAndMarkTab(activeVerticalTab);
        }
        toggleVerticalTab(tabId);
    };

    const handleNextTab = async () => {
        if (!isEdit) {
            const ok = await validateAndMarkTab(activeVerticalTab);
            if (!ok) {
                toast.warning('Complete los campos requeridos de esta pestaña antes de continuar.', { autoClose: 4000 });
                return;
            }
        }
        toggleVerticalTab(activeVerticalTab + 1);
    };

    function toggleTab(tab, value) {
        if (activeTab !== tab) {
            var modifiedSteps = [...passedSteps, tab];

            if (tab >= 1 && tab <= 4) {
                setactiveTab(tab);
                setPassedSteps(modifiedSteps);
            }
        }
        setprogressbarvalue(value);
    }

    function toggleArrowTab(tab) {
        if (activeArrowTab !== tab) {
            var modifiedSteps = [...passedarrowSteps, tab];

            if (tab >= 4 && tab <= 7) {
                setactiveArrowTab(tab);
                setPassedarrowSteps(modifiedSteps);
            }
        }
    }

    function toggleVerticalTab(tab) {
        if (activeVerticalTab !== tab) {
            var modifiedSteps = [...passedverticalSteps, tab];

            if (tab >= 7 && tab <= 14) {
                setactiveVerticalTab(tab);
                setPassedverticalSteps(modifiedSteps);
            }
        }
        // Persist draft on every tab change (create mode only — saveDraft is no-op in edit mode)
        saveDraft({
            formValues: validation.values,
            flowRatesList,
            intakeCoordinatesList,
            restitutionCoordinatesList,
            distanceElevationList,
        }, tab);
    }

    function navigateToErrorTab(errorFields) {
        const tabs = [7, 9, 10, 13, 14];
        for (const tabId of tabs) {
            if (errorFields.some(field => TAB_ERROR_FIELDS[tabId]?.includes(field))) {
                toggleVerticalTab(tabId);
                return;
            }
        }
    }

    function handleFormValidationErrors(errors) {
        updateAllTabStatuses(errors);
        toast.error('Por favor, corrige los errores en el formulario antes de continuar', { autoClose: 5000 });
        console.error('Errores de validación:', errors);
        navigateToErrorTab(Object.keys(errors));
    }

    const handleFinishRegistration = async () => {
        const errors = await validation.validateForm();
        validation.setTouched(
            Object.keys(validation.initialValues).reduce((acc, key) => ({ ...acc, [key]: true }), {})
        );
        const hasGridErrors =
            flowRatesList.length === 0 ||
            (intakeCoordinatesList.length === 0 && restitutionCoordinatesList.length === 0);

        if (Object.keys(errors).length > 0 || hasGridErrors) {
            updateAllTabStatuses(errors);
            if (flowRatesList.length === 0) {
                toast.warning('Debe agregar al menos un caudal en la pestaña "Distribución de Agua Autorizada"', { autoClose: 5000 });
                toggleVerticalTab(8);
            } else if (intakeCoordinatesList.length === 0 && restitutionCoordinatesList.length === 0) {
                toast.warning('Debe agregar al menos una coordenada de captación o restitución en la pestaña "Coordenadas"', { autoClose: 5000 });
                toggleVerticalTab(11);
            } else {
                handleFormValidationErrors(errors);
            }
            return;
        }
        validation.handleSubmit();
    };

    const handleResumeDraft = () => {
        if (!draftData) return;
        validation.setValues({
            ...draftData.formValues,
            resolution_date: parseDate(draftData.formValues.resolution_date),
            works_registration_date: parseDate(draftData.formValues.works_registration_date),
            report_transmission_start: parseDate(draftData.formValues.report_transmission_start),
        });
        setFlowRatesList(draftData.flowRatesList || []);
        setIntakeCoordinatesList(draftData.intakeCoordinatesList || []);
        setRestitutionCoordinatesList(draftData.restitutionCoordinatesList || []);
        setDistanceElevationList(draftData.distanceElevationList || []);
        setactiveVerticalTab(draftActiveTab || 7);
        setResumeDraftModalOpen(false);
    };

    const handleStartFresh = () => {
        clearDraft();
        setResumeDraftModalOpen(false);
    };

    const handleDiscardDraft = () => {
        setDiscardDraftModalOpen(true);
    };

    const handleConfirmDiscard = () => {
        clearDraft();
        setDiscardDraftModalOpen(false);
        validation.resetForm();
        setFlowRatesList([]);
        setIntakeCoordinatesList([]);
        setRestitutionCoordinatesList([]);
        setDistanceElevationList([]);
        setactiveVerticalTab(7);
        setTabStatuses(WIZARD_TABS.reduce((acc, id) => ({ ...acc, [id]: 'pending' }), {}));
    };


    return (
        <React.Fragment>
            <Row>
                <Col xl={12}>
                    <Card>
                        <CardHeader>
                            <h4 className="card-title mb-0">Registrar Derechos de Agua</h4>
                        </CardHeader>
                        <CardBody>
                            {!isEdit && (
                                <DraftStatusBar
                                    hasDraft={hasDraft}
                                    lastSaved={lastSaved}
                                    onDiscard={handleDiscardDraft}
                                />
                            )}
                            <form className="vertical-navs-step validation-tabs form-steps">
                                <Row className="gy-5">
                                    <Col lg={3}>
                                        <Nav
                                            className="flex-column custom-nav nav-pills"
                                        >
                                            {WIZARD_TAB_ITEMS.map(({ id, label }) => (
                                                <NavItem key={id}>
                                                    <NavLink
                                                        href="#"
                                                        className={getTabNavClass(id)}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            handleTabNavigation(id);
                                                        }}
                                                    >
                                                        <span className="step-title me-2">
                                                            <i className={getTabIconClass(id)}></i>
                                                        </span>
                                                        {label}
                                                    </NavLink>
                                                </NavItem>
                                            ))}
                                        </Nav>
                                    </Col>
                                    <Col lg={9} className="d-flex flex-column">
                                        <div className="flex-grow-1 px-lg-4">
                                            <TabContent activeTab={activeVerticalTab}>
                                                {activeVerticalTab === 7 && (
                                                <TabPane tabId={7}>
                                                    <GeneralTab
                                                        validation={validation}
                                                        handleSector={handleSectorSelected}
                                                    />
                                                </TabPane>
                                                )}
                                                {activeVerticalTab === 8 && (
                                                <TabPane tabId={8}>
                                                    <ListOfFlowRates
                                                        flowRatesList={flowRatesList}
                                                        setFlowRatesList={setFlowRatesList}
                                                        formIsEdit={isEdit}
                                                        waterRightId={waterRightData?._id}
                                                        selectedWaterSourceIds={validation.values.water_source_id || []}
                                                    />
                                                </TabPane>
                                                )}
                                                {activeVerticalTab === 9 && (
                                                <TabPane tabId={9}>
                                                    <FlowInformation validation={validation} />
                                                </TabPane>
                                                )}
                                                {activeVerticalTab === 10 && (
                                                <TabPane tabId={10}>
                                                    <RegistrationAndProcessing validation={validation} />
                                                </TabPane>
                                                )}
                                                {activeVerticalTab === 11 && (
                                                <TabPane tabId={11}>
                                                    <Coordinates
                                                        intakeCoordinatesList={intakeCoordinatesList}
                                                        setIntakeCoordinatesList={setIntakeCoordinatesList}
                                                        restitutionCoordinatesList={restitutionCoordinatesList}
                                                        setRestitutionCoordinatesList={setRestitutionCoordinatesList}
                                                        formIsEdit={isEdit}
                                                        waterRightId={waterRightData?._id}
                                                    />
                                                </TabPane>
                                                )}
                                                {activeVerticalTab === 12 && (
                                                <TabPane tabId={12}>
                                                    <ListDistancesAndElevation
                                                        distanceElevationList={distanceElevationList}
                                                        setDistanceElevationList={setDistanceElevationList}
                                                        formIsEdit={isEdit}
                                                        waterRightId={waterRightData?._id}
                                                        intakeCoordinatesList={intakeCoordinatesList}
                                                        restitutionCoordinatesList={restitutionCoordinatesList}
                                                    />
                                                </TabPane>
                                                )}
                                                {activeVerticalTab === 13 && (
                                                <TabPane tabId={13}>
                                                    <RegistrationOfWaterOwnership validation={validation} />
                                                </TabPane>
                                                )}
                                                {activeVerticalTab === 14 && (
                                                <TabPane tabId={14}>
                                                    <CPA_DGA validation={validation} />
                                                </TabPane>
                                                )}
                                            </TabContent>
                                        </div>
                                        <div className="d-flex justify-content-between mt-4 px-4">
                                            {activeVerticalTab > 7 && (
                                                <Button
                                                    className='btn btn-danger'
                                                    onClick={() => {
                                                        toggleVerticalTab(activeVerticalTab - 1);
                                                    }}
                                                >
                                                    Atras
                                                </Button>
                                            )}
                                            <div className="ms-auto btn-primary">
                                                {activeVerticalTab < 14 && (
                                                    <Button
                                                        color='primary'
                                                        onClick={handleNextTab}
                                                    >
                                                        Siguiente
                                                    </Button>
                                                )}
                                                {activeVerticalTab === 14 && (
                                                    <button
                                                        type='button'
                                                        className='btn btn-label btn-primary'
                                                        onClick={handleFinishRegistration}
                                                    >
                                                        <i className='ri-check-line label-icon'></i>
                                                        Finalizar Registro
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </form>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            {/* Resume Draft Modal */}
            <Modal isOpen={resumeDraftModalOpen} toggle={handleStartFresh} centered size='sm' backdrop='static'>
                <ModalHeader toggle={handleStartFresh}>Borrador guardado</ModalHeader>
                <ModalBody>
                    <p>Tienes un borrador guardado de un formulario incompleto. ¿Deseas continuar donde lo dejaste o empezar de nuevo?</p>
                </ModalBody>
                <ModalFooter>
                    <Button color='secondary' outline onClick={handleStartFresh}>Empezar de nuevo</Button>
                    <Button color='primary' onClick={handleResumeDraft}>Continuar borrador</Button>
                </ModalFooter>
            </Modal>

            {/* Discard Draft Confirmation Modal */}
            <Modal isOpen={discardDraftModalOpen} toggle={() => setDiscardDraftModalOpen(false)} centered size='sm'>
                <ModalHeader toggle={() => setDiscardDraftModalOpen(false)}>Confirmar descarte</ModalHeader>
                <ModalBody>
                    <p>¿Estás seguro de que deseas descartar el borrador? Se perderán todos los datos ingresados.</p>
                </ModalBody>
                <ModalFooter>
                    <Button color='secondary' outline onClick={() => setDiscardDraftModalOpen(false)}>Cancelar</Button>
                    <Button color='danger' onClick={handleConfirmDiscard}>Descartar borrador</Button>
                </ModalFooter>
            </Modal>
        </React.Fragment>
    );
};

export default FlowRatesForm;
